import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'

import { useVerificationAttachmentSignedUrl } from 'modules/resultsV2/hooks'
import { DataAccessRequestAttachmentSubmission } from 'services/data-access-requests/enums'
import { useDataAccessRequestVerificationAttachmentSignedUrl } from 'services/data-access-requests/hooks/useDataAccessRequestVerificationAttachmentSignedUrl'
import { DataAccessRequestQueryKeys } from 'services/data-access-requests/queryKeys'
import { QueryKeys } from 'services/queryKeys'
import { UploadedFileProvider } from 'services/results/enums'
import { type Result } from 'services/results/types'
import { isAdditionalInfoFormEnabled } from 'utils/env'

type CustomFormSubmission = NonNullable<Result['formSubmission']>
type CustomFormBlock = CustomFormSubmission['blocks'][number]
type CustomFormItem = CustomFormBlock['items'][number]

export interface FormFile {
  id: string
  name: string
  size?: string
  fieldId?: string
  attachmentIndex?: number
  url?: string
  bucket?: string
  fileKey?: string
  containerName?: string
  prefix?: string
  provider?: UploadedFileProvider
  storageAccountName?: string
}

type FormFieldType = 'information-panel' | 'checkbox' | 'checkbox-group' | 'file-upload' | 'tags' | 'text'

export interface FormField {
  id: string
  type: FormFieldType
  label: string
  helpText?: string
  value?: string
  values?: string[]
  files?: FormFile[]
  isChecked?: boolean
  alertMessage?: string
}

export interface FormSectionBlock {
  id: string
  title?: string
  fields: FormField[]
}

export type VerificationAttachmentSignedUrlSource = 'result' | 'data-access-request'

interface UseViewFormModalParams {
  formSubmission?: CustomFormSubmission
  resultId?: string
  resultBucket?: string
  verificationAttachmentSignedUrlSource?: VerificationAttachmentSignedUrlSource
  attachmentSubmission?: DataAccessRequestAttachmentSubmission
}

interface UseViewFormModalReturn {
  title: string
  formLogoUrl?: string
  blocks: FormSectionBlock[]
  hasSubmission: boolean
  selectedFileToPreview: FilePreview | null
  selectedAttachmentPreview: FormFile | null
  selectedSignedUrlPreview: FormFile | null
  previewTitle: string
  isPreviewOpen: boolean
  verificationAttachmentSignedUrlQuery: ReturnType<typeof useVerificationAttachmentSignedUrl>
  onPreviewFile: (file: FormFile) => void
  onClosePreview: () => void
}

export const FALLBACK_FIELD_VALUE = 'Not provided'

export type FilePreview = {
  file: S3FileAsContent
  bucket?: string
  fileKey: string
  containerName?: string
  provider?: UploadedFileProvider
  storageAccountName?: string
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null
}

const parseJson = (value: string): unknown => {
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

const normalizeValue = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value
  }

  const trimmedValue = value.trim()
  if (!trimmedValue.length) {
    return ''
  }

  return parseJson(trimmedValue)
}

const mapToDisplayArray = (value: unknown): string[] => {
  const normalizedValue = normalizeValue(value)

  if (Array.isArray(normalizedValue)) {
    return normalizedValue
      .map(item => {
        if (typeof item === 'string') {
          return item.trim()
        }

        if (isRecord(item)) {
          const label = item.label
          if (typeof label === 'string') {
            return label.trim()
          }

          const name = item.name
          if (typeof name === 'string') {
            return name.trim()
          }
        }

        return String(item).trim()
      })
      .filter(Boolean)
  }

  if (typeof normalizedValue === 'string') {
    return normalizedValue
      .split(',')
      .map(item => item.trim())
      .filter(Boolean)
  }

  if (normalizedValue === null || normalizedValue === undefined) {
    return []
  }

  return [String(normalizedValue)]
}

const mapToDisplayText = (value: unknown): string => {
  const normalizedValue = normalizeValue(value)

  if (normalizedValue === null || normalizedValue === undefined || normalizedValue === '') {
    return FALLBACK_FIELD_VALUE
  }

  if (Array.isArray(normalizedValue)) {
    const values = mapToDisplayArray(normalizedValue)
    return values.length ? values.join(', ') : FALLBACK_FIELD_VALUE
  }

  if (typeof normalizedValue === 'boolean') {
    return normalizedValue ? 'Yes' : 'No'
  }

  if (isRecord(normalizedValue)) {
    if (typeof normalizedValue.label === 'string') {
      return normalizedValue.label
    }

    if (typeof normalizedValue.name === 'string') {
      return normalizedValue.name
    }
  }

  const textValue = String(normalizedValue).trim()
  return textValue || FALLBACK_FIELD_VALUE
}

const mapToFiles = (item: CustomFormItem): FormFile[] => {
  const normalizedValue = normalizeValue(item.value)
  const fileUrlsByName = new Map<string, string>()

  if (Array.isArray(normalizedValue)) {
    normalizedValue.forEach(fileValue => {
      if (
        isRecord(fileValue) &&
        typeof fileValue.name === 'string' &&
        typeof fileValue.url === 'string' &&
        fileValue.url.trim()
      ) {
        fileUrlsByName.set(fileValue.name, fileValue.url)
      }
    })
  }

  if (item.uploadedFiles?.length) {
    return item.uploadedFiles.map((file, attachmentIndex) => {
      if (file.provider === 'AWS') {
        return {
          id: file.prefix,
          name: file.originalFileName,
          size: `${file.sizeInBytes}`,
          fieldId: item.fieldId,
          attachmentIndex,
          bucket: file.bucket,
          fileKey: file.prefix,
          url: fileUrlsByName.get(file.originalFileName)
        }
      }

      return {
        id: file.prefix,
        name: file.originalFileName,
        size: `${file.sizeInBytes}`,
        fieldId: item.fieldId,
        attachmentIndex,
        containerName: file.blobContainerName,
        prefix: file.prefix,
        provider: file.provider,
        storageAccountName: file.blobStorageAccountName,
        url: fileUrlsByName.get(file.originalFileName)
      }
    })
  }

  if (!Array.isArray(normalizedValue)) return []

  const parsedFiles: Array<FormFile | null> = normalizedValue.map(fileValue => {
    if (!isRecord(fileValue) || typeof fileValue.name !== 'string') {
      return null
    }

    return {
      id: typeof fileValue.id === 'string' ? fileValue.id : fileValue.name,
      name: fileValue.name,
      size:
        typeof fileValue.size === 'number'
          ? `${fileValue.size}`
          : typeof fileValue.size === 'string'
          ? fileValue.size
          : undefined,
      url: typeof fileValue.url === 'string' ? fileValue.url : undefined
    }
  })

  return parsedFiles.filter((file): file is FormFile => file !== null)
}

const isChecked = (value: unknown): boolean => {
  const normalizedValue = normalizeValue(value)

  if (typeof normalizedValue === 'boolean') {
    return normalizedValue
  }

  if (typeof normalizedValue === 'string') {
    return normalizedValue.toLowerCase() === 'true'
  }

  return false
}

const getSubmittedFieldValue = (item: CustomFormItem): unknown => {
  if (item.value !== null && item.value !== undefined && item.value !== '') {
    return item.value
  }

  if (Array.isArray(item.values) && item.values.length) {
    return item.values
  }

  return item.value
}

const getFieldHelpText = (item: CustomFormItem): string | undefined => {
  const helpText = item.helpText?.trim()
  return helpText || undefined
}

const mapItemToField = (item: CustomFormItem): FormField => {
  const type = item.type.toLowerCase()
  const helpText = getFieldHelpText(item)

  if (type === 'information-panel') {
    return {
      id: item.fieldId,
      type: 'information-panel',
      label: item.label,
      helpText,
      alertMessage: item.content || mapToDisplayText(item.value)
    }
  }

  if (type === 'checkbox') {
    return {
      id: item.fieldId,
      type: 'checkbox',
      label: item.label,
      helpText,
      isChecked: isChecked(item.value)
    }
  }

  if (type === 'checkbox-group') {
    return {
      id: item.fieldId,
      type: 'checkbox-group',
      label: item.label,
      helpText,
      values: mapToDisplayArray(getSubmittedFieldValue(item))
    }
  }

  if (type === 'file-upload') {
    return {
      id: item.fieldId,
      type: 'file-upload',
      label: item.label,
      helpText,
      files: mapToFiles(item)
    }
  }

  if ((type === 'select' && item.allowMultiple) || (Array.isArray(item.values) && item.values.length)) {
    const valuesSource = Array.isArray(item.values) && item.values.length ? item.values : getSubmittedFieldValue(item)

    return {
      id: item.fieldId,
      type: 'tags',
      label: item.label,
      helpText,
      values: mapToDisplayArray(valuesSource)
    }
  }

  if (type === 'select') {
    return {
      id: item.fieldId,
      type: 'text',
      label: item.label,
      helpText,
      value: mapToDisplayText(getSubmittedFieldValue(item))
    }
  }

  return {
    id: item.fieldId,
    type: 'text',
    label: item.label,
    helpText,
    value: mapToDisplayText(item.value)
  }
}

const mapBlock = (block: CustomFormBlock): FormSectionBlock => {
  const hasBlockItems = Array.isArray(block.items) && block.items.length > 0
  const blockItems = hasBlockItems ? block.items : block.item ? [block.item] : []

  return {
    id: block.id,
    title: block.title,
    fields: blockItems.map(mapItemToField)
  }
}

const useViewFormModal = ({
  formSubmission,
  resultId,
  resultBucket,
  verificationAttachmentSignedUrlSource = 'result',
  attachmentSubmission
}: UseViewFormModalParams): UseViewFormModalReturn => {
  const queryClient = useQueryClient()
  const [selectedFileToPreview, setSelectedFileToPreview] = useState<FilePreview | null>(null)
  const [selectedAttachmentPreview, setSelectedAttachmentPreview] = useState<FormFile | null>(null)
  const [selectedSignedUrlPreview, setSelectedSignedUrlPreview] = useState<FormFile | null>(null)

  const shouldFetchAttachmentSignedUrl = Boolean(
    resultId && selectedAttachmentPreview?.fieldId && selectedAttachmentPreview?.attachmentIndex !== undefined
  )

  const resultVerificationAttachmentSignedUrlQuery = useVerificationAttachmentSignedUrl(
    {
      resultId: resultId || '',
      fieldId: selectedAttachmentPreview?.fieldId || '',
      attachmentIndex: selectedAttachmentPreview?.attachmentIndex ?? 0
    },
    {
      enabled: verificationAttachmentSignedUrlSource === 'result' && shouldFetchAttachmentSignedUrl,
      retry: false
    }
  )

  const dataAccessRequestAttachmentSubmission =
    isAdditionalInfoFormEnabled() && attachmentSubmission ? attachmentSubmission : undefined

  const dataAccessRequestVerificationAttachmentSignedUrlQuery = useDataAccessRequestVerificationAttachmentSignedUrl(
    {
      requestId: resultId || '',
      fieldId: selectedAttachmentPreview?.fieldId || '',
      attachmentIndex: selectedAttachmentPreview?.attachmentIndex ?? 0,
      submission: dataAccessRequestAttachmentSubmission
    },
    {
      enabled: verificationAttachmentSignedUrlSource === 'data-access-request' && shouldFetchAttachmentSignedUrl,
      retry: false
    }
  )

  const verificationAttachmentSignedUrlQuery =
    verificationAttachmentSignedUrlSource === 'data-access-request'
      ? dataAccessRequestVerificationAttachmentSignedUrlQuery
      : resultVerificationAttachmentSignedUrlQuery

  const onPreviewFile = useCallback(
    (file: FormFile) => {
      if (resultId && file.fieldId && file.attachmentIndex !== undefined) {
        setSelectedFileToPreview(null)
        setSelectedSignedUrlPreview(null)
        setSelectedAttachmentPreview(file)
        return
      }

      if (file.url) {
        setSelectedFileToPreview(null)
        setSelectedAttachmentPreview(null)
        setSelectedSignedUrlPreview(file)
        return
      }

      if (file.bucket && file.fileKey) {
        setSelectedSignedUrlPreview(null)
        setSelectedAttachmentPreview(null)
        setSelectedFileToPreview({
          bucket: file.bucket,
          fileKey: file.fileKey,
          provider: file.provider,
          file: {
            name: file.name,
            size: Number(file.size) || 0,
            lastModified: new Date().toISOString()
          }
        })
        return
      }

      if (file.provider === 'Azure' && file.containerName && file.prefix && file.storageAccountName) {
        setSelectedSignedUrlPreview(null)
        setSelectedAttachmentPreview(null)
        setSelectedFileToPreview({
          bucket: resultBucket || file.containerName,
          fileKey: file.prefix,
          containerName: file.containerName,
          provider: file.provider,
          storageAccountName: file.storageAccountName,
          file: {
            name: file.name,
            size: Number(file.size) || 0,
            lastModified: new Date().toISOString()
          }
        })
      }
    },
    [resultBucket, resultId]
  )

  const onClosePreview = useCallback(() => {
    queryClient.cancelQueries({ queryKey: QueryKeys.GetFileContentsFromSignedUrl() })
    if (resultId && selectedAttachmentPreview?.fieldId && selectedAttachmentPreview.attachmentIndex !== undefined) {
      if (verificationAttachmentSignedUrlSource === 'data-access-request') {
        queryClient.cancelQueries({
          queryKey: DataAccessRequestQueryKeys.VerificationAttachmentSignedUrl({
            requestId: resultId,
            fieldId: selectedAttachmentPreview.fieldId,
            attachmentIndex: selectedAttachmentPreview.attachmentIndex,
            submission: dataAccessRequestAttachmentSubmission
          })
        })
      } else {
        queryClient.cancelQueries({
          queryKey: QueryKeys.GetVerificationAttachmentSignedUrl({
            resultId,
            fieldId: selectedAttachmentPreview.fieldId,
            attachmentIndex: selectedAttachmentPreview.attachmentIndex
          })
        })
      }
    }

    setSelectedFileToPreview(null)
    setSelectedAttachmentPreview(null)
    setSelectedSignedUrlPreview(null)
  }, [
    dataAccessRequestAttachmentSubmission,
    queryClient,
    resultId,
    selectedAttachmentPreview,
    verificationAttachmentSignedUrlSource
  ])

  const blocks = useMemo(() => {
    const submissionBlocks = formSubmission?.blocks
    const normalizedBlocks = Array.isArray(submissionBlocks) ? submissionBlocks : []

    return normalizedBlocks.map(mapBlock)
  }, [formSubmission])

  return {
    title: formSubmission?.formName ?? 'View Form',
    formLogoUrl: formSubmission?.logoUrl?.trim() || undefined,
    blocks,
    hasSubmission: !!formSubmission,
    selectedFileToPreview,
    selectedAttachmentPreview,
    selectedSignedUrlPreview,
    previewTitle:
      selectedAttachmentPreview?.name || selectedFileToPreview?.file.name || selectedSignedUrlPreview?.name || '',
    isPreviewOpen: !!selectedFileToPreview || !!selectedSignedUrlPreview || !!selectedAttachmentPreview,
    verificationAttachmentSignedUrlQuery,
    onPreviewFile,
    onClosePreview
  }
}

export default useViewFormModal
